<template>
	<ModalDialog
		:buttons="[[{name: 'previous', caption: $t('previous'), classes: 'btn-success', clickFn: previousClickedHandler, isDisabled: isFirstStep}], [{name: 'close', caption: $t('close'), classes: 'btn-outline-secondary', clickFn: cancelClickedHandler, isDisabled: false}], [{name: 'next', caption: $t('next'), classes: 'btn-success', clickFn: nextClickedHandler, isDisabled: isLastStep}]]"
		size="lg" class="modal-release-notes">
		<template v-slot:title>{{ $t('modal_show_release_notes_title') }}</template>
		<template v-slot:infoMessage>
			<span class="d-flex text-left mt-2">{{ $t('showingFeatureList') }}</span>
		</template>
		<template v-slot:body>
			<div class="pt-3" v-if="currentFeature">
				<p class="font-size-1rem">{{ $t(`releaseNotes_${currentFeature}FeatureTitle`) }}</p>
				<p>{{ $t('releaseNotes_' + currentFeature + 'FeatureText_0') }}</p>
				<template v-if="currentFeature === features[0].names[0]">
					<p class="text-center"><img src="../assets/img/releaseNotes/GeneralViewFilters.png" class="mw-100"></p>
				</template>
				<template v-else-if="currentFeature === features[0].names[1]">
					<p class="text-center"><img src="../assets/img/releaseNotes/CheckUpdates.png" class="mw-100"></p>
				</template>
				<template v-else-if="currentFeature === features[0].names[2]">
					<p class="text-center"><img src="../assets/img/releaseNotes/ProductViewFilters.png" class="mw-100"></p>
				</template>
				<template v-else-if="currentFeature === features[0].names[3]">
					<p class="text-center"><img src="../assets/img/releaseNotes/DocumentPackages.png" class="mw-100"></p>
				</template>
				<template v-else-if="currentFeature === features[0].names[4]">
					<p class="text-center"><img src="../assets/img/releaseNotes/VersionHistory.png" class="mw-100"></p>
				</template>
				<template v-else-if="currentFeature === features[0].names[5]">
					<p class="text-center"><img src="../assets/img/releaseNotes/LinkEdition.png" class="mw-100"></p>
				</template>
			</div>
		</template>
		<template v-slot:errorMessage v-if="!isCurrentUserExternalUser">
			<div class="font-weight-semibold p-3">{{ $t('release_notes_manual') }} <span class="clickable text-primary" @click="manualUserClickedHandler">{{ $t('userManual') }} </span></div>
		</template>
	</ModalDialog>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex';
import ModalDialog from './ModalDialog.vue';
import { CONSTANTS } from '../resources/constants.js';

export default {
	props: [],

	components: {
		ModalDialog,
	},

	data() {
		return {
			currentRelease: null,
			currentStep: null,
			features: null,
			firstRelease: null,
			hasSeenAllFeatures: false,
			localStorageDataInfo: {},
			objectInfo: {},
			startTime: null,
			timeInPages: [],
			timeInStep: null,
		};
	},

	computed: {
		...mapState(['releaseNotes']),
		...mapGetters(['isCurrentUserExternalUser']),

		currentFeature() {
			return this.features[this.currentRelease] && this.features[this.currentRelease].names && this.features[this.currentRelease].names[this.currentStep];
		},

		isFirstStep() {
			if (this.currentRelease !== this.firstRelease) {
				return false;
			}
			return this.currentStep === 0;
		},

		isLastRelease() {
			return this.currentRelease === (this.features.length - 1);
		},

		isLastStep() {
			return this.isLastRelease && this.currentStep === (this.features[this.features.length - 1].names.length - 1);
		},

	},

	methods: {
		...mapActions([
			'getLocalIP',
		]),

		...mapMutations([
			'setUserSettings',
			'updateUsageTrackerStorage',
		]),

		cancelClickedHandler() {
			const currentTime = this.$dayjs(), diffInModal = currentTime.diff(this.startTime),
				timeValues = Object.values(this.objectInfo);

			this.setObjectInfoTimeStep(currentTime);
			this.localStorageDataInfo.dateTutorialCompleted = null;
			this.objectInfo.dateTutorialCompleted = null;

			// only if the user has viewed the last information and, in addition, has spent a minimum time viewing the information, then the lastVisit value is updated, so that on the next visit the information will not appear again.
			if (this.hasSeenAllFeatures && !timeValues.some((t) => t < this.releaseNotes.minimunTimeOnModalPages)) {
				this.localStorageDataInfo.dateTutorialCompleted = currentTime.format('YYYYMMDD');
				this.objectInfo.dateTutorialCompleted = currentTime.format('YYYYMMDD');
			}

			this.objectInfo.totalTimeInModal = diffInModal;
			this.$emit('cancelClicked');

			this.setUserSettings({ field: CONSTANTS.UserSettings.fieldsKeys.releaseNotes, array: this.localStorageDataInfo });

			this.getLocalIP().then((localIp) => {
				this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.releaseNotes, data: this.objectInfo, localIp });
			});
		},

		manualUserClickedHandler() {
			window.open(CONSTANTS.UserManualUrl(this.$i18n.locale), '_blank').focus();
		},

		nextClickedHandler() {
			const currentTime = this.$dayjs();

			this.setObjectInfoTimeStep(currentTime);

			this.currentStep++;

			if (this.currentStep === this.features[this.currentRelease].names.length && this.currentRelease < this.features.length - 1) {
				this.currentStep = 0;
				this.currentRelease++;
			}

			this.timeInStep = currentTime;

			this.hasSeenAllFeatures = this.hasSeenAllFeatures || this.isLastStep;
		},

		previousClickedHandler() {
			const currentTime = this.$dayjs();

			this.setObjectInfoTimeStep(currentTime);

			this.currentStep--;

			if (this.currentStep < 0) {
				this.currentRelease--;
				this.currentStep = this.features[this.currentRelease].names.length - 1;
			}

			this.timeInStep = currentTime;
		},

		setObjectInfoTimeStep(currentTime) {
			const key = `timeInRelease_${this.features[this.currentRelease].names[this.currentStep]}`,
				currentValue = this.objectInfo[key] || 0,
				diffTime = currentTime.diff(this.timeInStep),
				totalTime = currentValue + diffTime;
			this.objectInfo[key] = totalTime;
		},
	},

	created() {
		this.features = this.releaseNotes.features;
	},

	mounted() {
		let { dateTutorialCompleted } = { ...this.releaseNotes.releaseNotesUserInfor };

		this.firstRelease = this.currentRelease = dateTutorialCompleted ? this.features.findIndex((f) => dateTutorialCompleted < f.featureDate) : 0;

		this.currentStep = 0;
		this.timeInStep = this.$dayjs();
		this.startTime = this.$dayjs();
	},
};
</script>
